import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Camera, CaretLeft, CircleNotch, Warning } from '@phosphor-icons/react'
import { Button } from '../../../components/ui/Button'
import { Badge } from '../../../components/ui/Badge'
import { useAuth } from '@/core/AuthContext'
import { PoseDetection } from '../api/poseDetection'
import type { PresentCameraResult } from '../api/poseDetection'
import { getNativePlatform } from '@/core/capacitor'

export function PostureCheckView() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const isNative = getNativePlatform() === 'ios'

  const [isLaunching, setIsLaunching] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const launchCamera = useCallback(async () => {
    if (!user || isLaunching) return

    setIsLaunching(true)
    setError(null)

    try {
      const result: PresentCameraResult = await PoseDetection.presentCamera()

      if (result.cancelled) {
        setIsLaunching(false)
        return
      }

      // Photo is saved to iOS Photos library by the native code.
      // We receive the local identifier which can be used to fetch
      // the photo later if needed.
      if (result.photoLocalIdentifier) {
        console.log('📸 Photo saved with ID:', result.photoLocalIdentifier)
        // TODO: Store reference in Firestore if needed for cloud sync
      }

      // Navigate to gallery after capture
      navigate('/client/progress/photos')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open camera')
      setIsLaunching(false)
    }
  }, [user, isLaunching, navigate])

  // Auto-launch camera on mount for native
  useEffect(() => {
    if (!isNative) {
      setError('Progress photos require an iOS device.')
      return
    }

    void launchCamera()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!user) return null

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="border-b border-slate-800 bg-slate-950 px-4 py-3 safe-top">
        <div className="mx-auto w-full max-w-sm">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/client/progress/photos')}
            className="mb-2"
          >
            <CaretLeft size={16} weight="bold" />
            Back to Gallery
          </Button>
          <Badge variant="info" size="sm" className="mb-1">
            Progress Photos
          </Badge>
          <h1 className="text-xl font-bold text-white">Capture check-in</h1>
        </div>
      </div>

      <main className="flex min-h-[calc(100vh-84px)] items-center justify-center px-6">
        <div className="mx-auto w-full max-w-sm text-center">
          <h1 className="text-2xl font-semibold">Progress Photos</h1>
          <p className="mt-2 text-sm text-slate-400">
            {isLaunching
              ? 'Opening camera...'
              : 'Capture a progress photo with pose detection.'}
          </p>

          {error && (
            <div className="mt-4 flex items-center justify-center gap-2 rounded-lg bg-red-500/20 px-4 py-3 text-sm text-red-300">
              <Warning size={18} weight="fill" />
              {error}
            </div>
          )}

          {isLaunching ? (
            <div className="mt-8 flex justify-center">
              <CircleNotch size={32} weight="bold" className="animate-spin text-orange-400" />
            </div>
          ) : (
            <div className="mt-8 flex flex-col gap-3">
              <Button onClick={() => void launchCamera()} disabled={!isNative}>
                <Camera size={18} weight="bold" />
                Open Camera
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
